import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CircleDollarSign, Eye, Pencil, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EnrollmentRequestsPanel } from "@/components/EnrollmentRequests";
import { Textarea } from "@/components/ui/textarea";
import { WRITE_ROLES, useAuth } from "@/contexts/AuthContext";
import { PedagogyPanel } from "@/components/PedagogyPanel";
import { PedagogicalReportDialog } from "@/components/PedagogicalReport";
import { addMinutes, api, fmtBR, formatApiError, WEEKDAY_NAMES } from "@/lib/api";

export const ENROLLMENT_STATUS = { ativa: "Ativa", suspensa: "Suspensa", cancelada: "Cancelada", concluida: "Concluída" };
const LESSON_STATUS = { agendada: "Agendada", realizada: "Realizada", cancelada: "Cancelada", remarcada: "Remarcada" };
const HISTORY_LABELS = { criacao: "Criação", alteracao: "Alteração", suspensao: "Suspensão", cancelamento: "Cancelamento", conclusao: "Conclusão", reativacao: "Reativação" };

export const EnrollmentStatusBadge = ({ status }) => (
  <Badge
    variant={status === "ativa" ? "default" : status === "cancelada" ? "destructive" : "secondary"}
    className={status === "ativa" ? "bg-primary text-primary-foreground" : ""}
  >
    {ENROLLMENT_STATUS[status] || status}
  </Badge>
);

const EMPTY_FORM = {
  student_id: "", course_id: "", teacher_id: "", modality_id: "", room_id: "",
  weekday: "", start_time: "", start_date: "", end_date: "", admin_notes: "", status: "ativa",
};

export default function Enrollments() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user.role);
  const canReview = ["admin", "secretaria"].includes(user.role);
  const [enrollments, setEnrollments] = useState([]);
  const [lookups, setLookups] = useState({ students: [], teachers: [], courses: [], rooms: [], modalities: [] });
  const [filters, setFilters] = useState({ search: "", teacher_id: "todos", course_id: "todos", status: "todos" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [conflicts, setConflicts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [generatingId, setGeneratingId] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const PAGE_SIZE = 25;

  const load = async () => {
    const params = { page, page_size: PAGE_SIZE };
    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.teacher_id !== "todos") params.teacher_id = filters.teacher_id;
    if (filters.course_id !== "todos") params.course_id = filters.course_id;
    if (filters.status !== "todos") params.status = filters.status;
    const requests = [
      api.get("/enrollments", { params }),
      api.get("/teachers"),
      api.get("/courses"),
      api.get("/rooms"),
      api.get("/modalities"),
    ];
    if (canWrite) requests.push(api.get("/students"));
    const [e, t, c, r, m, s] = await Promise.all(requests);
    setEnrollments(e.data.items);
    setMeta({ total: e.data.total, pages: Math.max(e.data.pages, 1) });
    setLookups({
      students: canWrite && s ? s.data.filter((x) => x.status === "ativo") : [],
      teachers: t.data.filter((x) => x.active),
      courses: c.data.filter((x) => x.active),
      rooms: r.data.filter((x) => x.active),
      modalities: m.data.filter((x) => x.active),
    });
  };

  // mudança de filtro/busca → volta para a primeira página
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.teacher_id, filters.course_id, filters.status]);

  useEffect(() => {
    load().catch((err) => toast.error(formatApiError(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.teacher_id, filters.course_id, filters.status]);

  const compatibleTeachers = useMemo(() => {
    if (!form.course_id) return [];
    return lookups.teachers.filter((t) => t.course_ids.includes(form.course_id));
  }, [form.course_id, lookups.teachers]);

  const selectedModality = lookups.modalities.find((m) => m.id === form.modality_id);
  const computedEnd = form.start_time && selectedModality ? addMinutes(form.start_time, selectedModality.duration_minutes) : null;

  const openDialog = (enrollment) => {
    setConflicts([]);
    if (enrollment) {
      setEditing(enrollment);
      setForm({
        student_id: enrollment.student_id, course_id: enrollment.course_id,
        teacher_id: enrollment.teacher_id, modality_id: enrollment.modality_id,
        room_id: enrollment.room_id, weekday: String(enrollment.weekday),
        start_time: enrollment.start_time, start_date: enrollment.start_date,
        end_date: enrollment.end_date || "", admin_notes: enrollment.admin_notes || "",
        status: enrollment.status,
      });
    } else {
      setEditing(null);
      setForm(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setConflicts([]);
    try {
      const payload = { ...form, weekday: Number(form.weekday) };
      if (!payload.end_date) delete payload.end_date;
      if (!payload.admin_notes) delete payload.admin_notes;
      if (editing) {
        const { data } = await api.put(`/enrollments/${editing.id}`, payload);
        toast.success(
          `Matrícula atualizada. ${data.lessons_cancelled ? `${data.lessons_cancelled} aula(s) futura(s) cancelada(s).` : ""} ${data.lessons_generated ? `${data.lessons_generated} aula(s) gerada(s).` : ""}`.trim()
        );
      } else {
        const { data } = await api.post("/enrollments", payload);
        toast.success(
          `Matrícula criada. ${data.lessons_generated} aula(s) gerada(s)${data.holidays_skipped ? `, ${data.holidays_skipped} pulada(s) por feriado` : ""}.`
        );
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && Array.isArray(detail)) setConflicts(detail);
      else setConflicts([formatApiError(err)]);
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (enrollment) => {
    try {
      const { data } = await api.get(`/enrollments/${enrollment.id}`);
      setDetail(data);
      if (canWrite) {
        const h = await api.get(`/enrollments/${enrollment.id}/history`);
        setHistory(h.data);
      } else {
        setHistory([]);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const generateCharge = async (enrollment) => {
    setGeneratingId(enrollment.id);
    try {
      const { data } = await api.post("/financial/charges/generate", { enrollment_id: enrollment.id });
      if (data.created > 0) {
        toast.success(`Cobrança de ${data.reference_month} gerada para ${enrollment.student_name}.`);
      } else {
        toast.info(`Cobrança de ${data.reference_month} já existia — nenhuma duplicada.`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGeneratingId("");
    }
  };

  return (
    <div className="space-y-6" data-testid="enrollments-page">
      <Tabs defaultValue="matriculas">
        {canReview && (
          <TabsList data-testid="enrollments-tabs">
            <TabsTrigger value="matriculas" data-testid="tab-matriculas">Matrículas</TabsTrigger>
            <TabsTrigger value="solicitacoes" data-testid="tab-solicitacoes">Solicitações de matrícula</TabsTrigger>
          </TabsList>
        )}
        <TabsContent value="matriculas" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Matrículas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Vínculo oficial: aluno → curso → professor → modalidade → horário → sala, com aulas semanais geradas automaticamente.
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => openDialog(null)} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="enrollment-add-button">
            <Plus size={16} className="mr-2" /> Nova matrícula
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-3 lg:flex-row">
        {canWrite && (
          <form onSubmit={(e) => { e.preventDefault(); if (page === 1) load(); else setPage(1); }} className="flex flex-1 items-center gap-2">
            <Input
              placeholder="Pesquisar por aluno..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              data-testid="enrollment-search-input"
            />
            <Button type="submit" variant="outline" size="icon" data-testid="enrollment-search-button">
              <Search size={16} />
            </Button>
          </form>
        )}
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
          <Select value={filters.teacher_id} onValueChange={(v) => setFilters({ ...filters, teacher_id: v })}>
            <SelectTrigger data-testid="enrollment-filter-teacher"><SelectValue placeholder="Professor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os professores</SelectItem>
              {lookups.teachers.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.course_id} onValueChange={(v) => setFilters({ ...filters, course_id: v })}>
            <SelectTrigger data-testid="enrollment-filter-course"><SelectValue placeholder="Curso" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os cursos</SelectItem>
              {lookups.courses.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="enrollment-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os status</SelectItem>
              {Object.entries(ENROLLMENT_STATUS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Aluno</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Curso</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider md:table-cell">Professor</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Dia / Horário</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Sala</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Início</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {enrollments.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="p-6 text-sm text-muted-foreground" data-testid="enrollments-empty">
                  Nenhuma matrícula encontrada.
                </TableCell>
              </TableRow>
            )}
            {enrollments.map((e) => (
              <TableRow key={e.id} className="transition-colors hover:bg-zinc-50" data-testid={`enrollment-row-${e.id}`}>
                <TableCell className="font-medium">
                  {e.student_name}
                  {e.student_age != null && <span className="ml-1 text-xs text-muted-foreground">({e.student_age} anos)</span>}
                </TableCell>
                <TableCell>{e.course_name}</TableCell>
                <TableCell className="hidden md:table-cell">{e.teacher_name}</TableCell>
                <TableCell>
                  {WEEKDAY_NAMES[e.weekday]} · {e.start_time}–{e.end_time}
                </TableCell>
                <TableCell className="hidden lg:table-cell">{e.room_name}</TableCell>
                <TableCell className="hidden lg:table-cell">{fmtBR(e.start_date)}</TableCell>
                <TableCell><EnrollmentStatusBadge status={e.status} /></TableCell>
                <TableCell className="text-right">
                  {canWrite && e.status === "ativa" && (
                    <Button variant="ghost" size="icon" title="Gerar cobrança do mês atual" disabled={generatingId === e.id} onClick={() => generateCharge(e)} data-testid={`enrollment-generate-charge-${e.id}`}>
                      <CircleDollarSign size={16} />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" title="Detalhes" onClick={() => openDetail(e)} data-testid={`enrollment-view-${e.id}`}>
                    <Eye size={16} />
                  </Button>
                  {canWrite && (
                    <Button variant="ghost" size="icon" title="Editar" onClick={() => openDialog(e)} data-testid={`enrollment-edit-${e.id}`}>
                      <Pencil size={16} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex flex-wrap items-center justify-between gap-2" data-testid="enrollment-pagination">
          <p className="text-sm text-muted-foreground" data-testid="enrollment-page-info">
            {meta.total} matrículas · Página {page} de {meta.pages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="enrollment-prev-page">
              Anterior
            </Button>
            <Button variant="outline" size="sm" disabled={page >= meta.pages} onClick={() => setPage(page + 1)} data-testid="enrollment-next-page">
              Próxima
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="enrollment-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar matrícula" : "Nova matrícula"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {conflicts.length > 0 && (
              <div className="border border-destructive/50 bg-destructive/10 p-3" data-testid="enrollment-conflicts-alert">
                <p className="flex items-center gap-2 text-sm font-semibold text-destructive">
                  <AlertTriangle size={16} /> Não foi possível salvar
                </p>
                <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-destructive">
                  {conflicts.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="space-y-2">
              <Label>Aluno *</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="enrollment-student-select"><SelectValue placeholder="Selecione o aluno" /></SelectTrigger>
                <SelectContent>
                  {lookups.students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.age} anos)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Curso *</Label>
                <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v, teacher_id: "" })}>
                  <SelectTrigger data-testid="enrollment-course-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {lookups.courses.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Professor *</Label>
                <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })} disabled={!form.course_id}>
                  <SelectTrigger data-testid="enrollment-teacher-select">
                    <SelectValue placeholder={form.course_id ? "Professores compatíveis" : "Escolha o curso primeiro"} />
                  </SelectTrigger>
                  <SelectContent>
                    {compatibleTeachers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Modalidade *</Label>
                <Select value={form.modality_id} onValueChange={(v) => setForm({ ...form, modality_id: v })}>
                  <SelectTrigger data-testid="enrollment-modality-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {lookups.modalities.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name} — {m.duration_minutes} min (R$ {m.monthly_price.toFixed(2)}/mês)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sala *</Label>
                <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
                  <SelectTrigger data-testid="enrollment-room-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {lookups.rooms.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Dia da semana *</Label>
                <Select value={form.weekday} onValueChange={(v) => setForm({ ...form, weekday: v })}>
                  <SelectTrigger data-testid="enrollment-weekday-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {WEEKDAY_NAMES.map((n, i) => (
                      <SelectItem key={i} value={String(i)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Horário de início *</Label>
                <Input type="time" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="enrollment-start-input" />
              </div>
            </div>
            {computedEnd && (
              <p className="border border-border bg-zinc-50 p-2 text-sm" data-testid="enrollment-computed-end">
                Término calculado: <strong>{computedEnd}</strong> ({selectedModality.duration_minutes} min · 1 aula por semana)
              </p>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Data de início *</Label>
                <Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="enrollment-start-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Data de término (opcional)</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="enrollment-end-date-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Observações administrativas</Label>
              <Textarea rows={2} value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} data-testid="enrollment-notes-input" />
            </div>
            {editing && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="enrollment-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ENROLLMENT_STATUS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Suspender, cancelar ou concluir cancela as aulas futuras. O histórico de aulas passadas é preservado.
                </p>
              </div>
            )}
            <Button
              type="submit"
              disabled={saving || !form.student_id || !form.course_id || !form.teacher_id || !form.modality_id || !form.room_id || form.weekday === "" || !form.start_time || !form.start_date}
              className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
              data-testid="enrollment-save-button"
            >
              {saving ? "Salvando..." : editing ? "Salvar alterações" : "Criar matrícula e gerar aulas"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="enrollment-detail-dialog">
          <DialogHeader>
            <DialogTitle>Matrícula — {detail?.student_name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Curso</p><p className="font-medium">{detail.course_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Professor</p><p className="font-medium">{detail.teacher_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Modalidade</p><p className="font-medium">{detail.modality_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Horário</p><p className="font-medium">{WEEKDAY_NAMES[detail.weekday]} · {detail.start_time}–{detail.end_time}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Sala</p><p className="font-medium">{detail.room_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Status</p><EnrollmentStatusBadge status={detail.status} /></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Início</p><p className="font-medium">{fmtBR(detail.start_date)}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Término</p><p className="font-medium">{detail.end_date ? fmtBR(detail.end_date) : "Indeterminado"}</p></div>
                {detail.monthly_price != null && (
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Mensalidade</p><p className="font-medium" data-testid="enrollment-detail-price">R$ {detail.monthly_price.toFixed(2)} (fixa)</p></div>
                )}
              </div>

              <div>
                <h3 className="font-heading text-base font-semibold">Aulas relacionadas</h3>
                <div className="mt-2 max-h-64 overflow-y-auto border border-border" data-testid="enrollment-lessons-list">
                  {detail.lessons.length === 0 && (
                    <p className="p-4 text-sm text-muted-foreground">Nenhuma aula gerada.</p>
                  )}
                  {detail.lessons.map((l) => (
                    <div key={l.id} className="flex items-center justify-between border-b border-border px-3 py-2 text-sm last:border-b-0" data-testid={`enrollment-lesson-${l.id}`}>
                      <span className={l.status === "cancelada" ? "line-through opacity-60" : ""}>
                        {fmtBR(l.date)} · {l.start_time}–{l.end_time}
                      </span>
                      <Badge variant={l.status === "agendada" ? "default" : l.status === "cancelada" ? "destructive" : "secondary"} className={l.status === "agendada" ? "bg-primary text-primary-foreground" : ""}>
                        {LESSON_STATUS[l.status] || l.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-heading text-base font-semibold">Objetivos pedagógicos</h3>
                  <Button size="sm" variant="outline" onClick={() => setReportOpen(true)} data-testid="enrollment-report-button">
                    Relatório pedagógico
                  </Button>
                </div>
                <div className="mt-2">
                  <PedagogyPanel enrollmentId={detail.id} testIdPrefix="enrollment-pedagogy" />
                </div>
              </div>

              {history.length > 0 && (
                <div>
                  <h3 className="font-heading text-base font-semibold">Histórico</h3>
                  <div className="mt-2 space-y-2" data-testid="enrollment-history-list">
                    {history.map((h) => (
                      <div key={h.id} className="border border-border p-2 text-xs">
                        <span className="font-semibold">{HISTORY_LABELS[h.action] || h.action}</span>
                        <span className="text-muted-foreground"> · {h.user_name} · {new Date(h.created_at).toLocaleString("pt-BR")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {reportOpen && detail && (
        <PedagogicalReportDialog studentId={detail.student_id} studentName={detail.student_name} onClose={() => setReportOpen(false)} />
      )}
        </TabsContent>
        {canReview && (
          <TabsContent value="solicitacoes">
            <EnrollmentRequestsPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
